import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { Search, Eye, ToggleLeft, ToggleRight, Filter } from 'lucide-react';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (levelFilter !== '') params.append('level', levelFilter);
      if (activeFilter !== '') params.append('isActive', activeFilter);
      const { data } = await axios.get(`/api/admin/users?${params}`);
      setUsers(data.users);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [page, levelFilter, activeFilter]);
  useEffect(() => {
    const t = setTimeout(() => fetchUsers(), 400);
    return () => clearTimeout(t);
  }, [search]);

  const toggleActive = async (userId, currentStatus) => {
    try {
      await axios.put(`/api/admin/users/${userId}/toggle`);
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Users ({total})</h1>
          <p className="text-gray-400 mt-1">Manage all registered users</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-dark-700 border border-white/5 text-white rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:border-primary-500 text-sm"
            />
          </div>
          <select value={levelFilter} onChange={e => { setLevelFilter(e.target.value); setPage(1); }}
            className="bg-dark-700 border border-white/5 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500">
            <option value="">All Levels</option>
            {[0,1,2,3,4,5,6,7].map(l => <option key={l} value={l}>Level {l}</option>)}
          </select>
          <select value={activeFilter} onChange={e => { setActiveFilter(e.target.value); setPage(1); }}
            className="bg-dark-700 border border-white/5 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500">
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">User</th>
                  <th className="text-center px-4 py-3 text-gray-400 text-sm font-medium">Level</th>
                  <th className="text-right px-4 py-3 text-gray-400 text-sm font-medium">Main Wallet</th>
                  <th className="text-right px-4 py-3 text-gray-400 text-sm font-medium">Commission</th>
                  <th className="text-center px-4 py-3 text-gray-400 text-sm font-medium">Team</th>
                  <th className="text-center px-4 py-3 text-gray-400 text-sm font-medium">MT5</th>
                  <th className="text-center px-4 py-3 text-gray-400 text-sm font-medium">Status</th>
                  <th className="text-center px-4 py-3 text-gray-400 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-500">Loading...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-500">No users found</td></tr>
                ) : users.map(u => (
                  <tr key={u._id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-bold text-sm flex-shrink-0">
                          {u.name[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{u.name}</p>
                          <p className="text-gray-500 text-xs">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="bg-primary-500/20 text-primary-300 text-xs px-2 py-0.5 rounded-full">L{u.level}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-white text-sm">${(u.mainWalletBalance || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-primary-400 text-sm">${(u.commissionWalletBalance || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-center text-gray-300 text-sm">{u.directReferrals}/{u.teamSize}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`w-2 h-2 rounded-full inline-block ${u.mt5Connected ? 'bg-primary-500' : 'bg-gray-600'}`}></span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.isActive ? 'bg-primary-500/20 text-primary-300' : 'bg-red-500/20 text-red-300'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Link to={`/admin/users/${u._id}`}
                          className="w-8 h-8 bg-dark-600 hover:bg-dark-500 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                          <Eye className="w-3.5 h-3.5" />
                        </Link>
                        <button onClick={() => toggleActive(u._id, u.isActive)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${u.isActive ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' : 'bg-primary-500/20 hover:bg-primary-500/30 text-primary-400'}`}>
                          {u.isActive ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > 20 && (
            <div className="p-4 border-t border-white/5 flex items-center justify-between">
              <p className="text-gray-400 text-sm">Showing {((page-1)*20)+1}–{Math.min(page*20, total)} of {total}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                  className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Prev</button>
                <button onClick={() => setPage(p => p+1)} disabled={page*20>=total}
                  className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
